import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, Signal, signal, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FieldValueInterface, FormInputComponent, InlineEditService, ObjectType } from '@galaxy/crm/static';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { tap } from 'rxjs';
import {
  PartnerCRMSalespersonService,
  salespersonFilterFn,
  SalespersonWithDisplayName,
} from '../../salesperson.service';

const NO_VALUE_FOR_SALESPERSON = '<NO_VALUE_FOR_SALESPERSON>';

@Component({
  templateUrl: './salesperson-form-input.component.html',
  providers: [],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GalaxyFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
  ],
  styleUrls: ['../shared.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
})
export class SalespersonFormInputComponent extends FormInputComponent {
  NO_VALUE_FOR_SALESPERSON = NO_VALUE_FOR_SALESPERSON;

  readOnly = false;
  objectType: ObjectType = 'Contact';
  _control: FormControl<SalespersonWithDisplayName | string> = new FormControl<SalespersonWithDisplayName | string>(
    '',
    {
      nonNullable: true,
    },
  );
  _label = '';
  searchTerm = signal<SalespersonWithDisplayName | string>('');
  set control(control: FormControl<SalespersonWithDisplayName | string>) {
    this._control = control;
    this._control.setValue(this._control.value || '');
    this.searchTerm.set(control.value);
    this._control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newValue) => this.searchTerm.set(newValue));
  }
  get control(): UntypedFormControl {
    return this._control;
  }

  _fieldId = signal('');
  set fieldId(fieldId: string) {
    this._fieldId.set(fieldId);
  }

  get hasModification(): boolean {
    return this._control.dirty;
  }

  set label(label: string) {
    this._label = label;
  }

  get label(): string {
    return this._label;
  }

  get value(): FieldValueInterface {
    const controlValue: SalespersonWithDisplayName | string = this._control.value;
    if (typeof controlValue === 'string') {
      return {
        fieldId: this._fieldId(),
        stringValue: controlValue,
      };
    }
    return {
      fieldId: this._fieldId(),
      stringValue: controlValue?.salespersonId,
    };
  }

  loadingValues = signal(true);
  availableOptions = signal<SalespersonWithDisplayName[]>([]);
  filteredOptions: Signal<SalespersonWithDisplayName[]> = computed(() => {
    const searchTerm = this.searchTerm();
    const availableOptions = this.availableOptions();
    const filterTerm = typeof searchTerm === 'string' ? searchTerm : searchTerm?.salespersonId;
    return availableOptions.filter((salesperson) => salespersonFilterFn(salesperson, filterTerm));
  });
  salespersonName = computed(() => {
    const searchTerm = this.searchTerm();
    const availableOptions = this.availableOptions();
    const filterTerm = typeof searchTerm === 'string' ? searchTerm : searchTerm?.salespersonId;
    if (!searchTerm) {
      return NO_VALUE_FOR_SALESPERSON;
    }
    const foundSalesperson = availableOptions.find((salesperson) => salespersonFilterFn(salesperson, filterTerm));
    return foundSalesperson?.displayName || searchTerm;
  });

  constructor(
    private readonly salespersonService: PartnerCRMSalespersonService,
    private readonly destroyRef: DestroyRef,
    private readonly inlineEditService: InlineEditService,
  ) {
    super();
    this.salespersonService.allSalespeople$$
      .pipe(
        takeUntilDestroyed(),
        tap(() => this.loadingValues.set(true)),
      )
      .subscribe((allSalespeople) => {
        this.availableOptions.set(allSalespeople);
        this.loadingValues.set(false);
      });
  }

  displayFn(
    availableOptions: SalespersonWithDisplayName[],
  ): (salesperson: SalespersonWithDisplayName | string) => string {
    return (salesperson: SalespersonWithDisplayName | string): string => {
      if (!salesperson) {
        return '';
      }
      if (typeof salesperson === 'string') {
        const foundSalesperson = availableOptions.find((sp) => sp.salespersonId === salesperson);
        return foundSalesperson?.displayName || salesperson;
      }
      return salesperson?.displayName;
    };
  }

  saveField(): void {
    this.inlineEditService.saveInlineEdit(this.objectType, this.value);
  }
}
